import { Component, effect, OnInit, signal, Signal, input, OutputRefSubscription, OnDestroy, WritableSignal } from '@angular/core';

import { DEFAULT_IMAGE_ALT_TEXT } from '../../../../state-management/constants/global.constants';
import { FileType } from '../../../../state-management/model/enums/state.enums';
import { FormatMapInput, ImageCoordinates } from '../../../../state-management/model/interfaces/screen-size.model';
import { CoordinatesSystem, MaxAspectRatioBreakpoints, ImageFormat, ScreenWidthBreakpoints, DeviceSpecificBreakpoints, NoBreakpoints } from '../../../../state-management/model/enums/screen-size.enums';
import { ButtonActionsComponent } from '../../button/button-actions/button-actions.component';

import { CommonModule, NgOptimizedImage } from '@angular/common';

import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { ScreensizeStateService } from '../../../../state-management/services/screensize-state.service';
import { ScrollService } from '../../../../services/scroll.service';
import { ClickOutsideDirective } from '../../../../directives/click-outside.directive';

import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ImageCropComponent } from '../image-crop/image-crop.component';
import { Subscription, timeout } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-editable-image',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatButtonModule,
    ButtonActionsComponent,
    ClickOutsideDirective,
    NgOptimizedImage,
    MatProgressSpinner,
  ],
  templateUrl: './editable-image.component.html',
  styleUrls: ['./editable-image.component.scss']
})
export class EditableImageComponent implements OnInit, OnDestroy {
  readonly elemId = input.required<string>();
  readonly fileTypes = input.required<FileType[]>();
  readonly imageReference = input.required<ImageCoordinates>();
  readonly customClass = input<string | null>(null);
  readonly maxWidth = input<number | null>(null);
  readonly maxHeight = input<number | null>(null);
  readonly isClickable = input<boolean>(false);
  readonly topSpacing = input<string>('0.75rem');
  readonly forceEditMode = input<boolean>(false);
  readonly hasPriority = input<boolean>(false);

  private shopID: Signal<string | null> = this.stateService.shopId;
  private overlayRef: OverlayRef | null = null;
  private overlaySubscription: Subscription = new Subscription();

  public isEditMode$: Signal<boolean> = this.stateService.isEditMode;
  public screenSize$: Signal<Record<CoordinatesSystem, MaxAspectRatioBreakpoints | DeviceSpecificBreakpoints | ScreenWidthBreakpoints | NoBreakpoints>> = this.stateScreenSize.screenSizes;
  public screenSizeChanges$: Signal<number> = this.stateScreenSize.screenSizeChanges;
  public imageSRC$: WritableSignal<Record<string, string>> = signal<Record<string, string>>({});
  public altText$: Signal<string> = signal<string>(DEFAULT_IMAGE_ALT_TEXT);

  public requestedFormats: ImageFormat[] = [];
  public showEditButton: boolean = false;
  public isInteractionConfigOpen: boolean = false;

  public reloadingImage: boolean = false;

  constructor(
    private overlay: Overlay,
    private scrollService: ScrollService,
    private stateService: WebsiteStateService,
    private stateScreenSize: ScreensizeStateService,
  ) {
    this.#initializeEffects();
  }

  ngOnInit(): void {
    this.#initializeRequestedFormats();
    this.#initializeAltText();
    this.#initURLs();
  }

  ngOnDestroy(): void {
    if (this.overlaySubscription) this.overlaySubscription.unsubscribe();
  }

  // ### Public methods ###
  public onClickOutside(): void {
    this.closeAllConfig();
  }

  public closeAllConfig(): void {
    this.isInteractionConfigOpen = false;
    this.showEditButton = false;
  }

  public onClick(): void {
    if (this.isEditMode$()) {
      this.showEditButton = true;
    } else if (this.isClickable()) {
      this.scrollService.onClick(this.elemId());
    }
  }

  public openImageCropper($event: Event): void {
    $event.stopPropagation();
    this.isInteractionConfigOpen = false;
    this.openPortal();
  }

  public openInteraction($event: Event): void {
    $event.stopPropagation();
    this.isInteractionConfigOpen = true;
    this.showEditButton = true;
  }


  // ### Private methods ###
  #initializeEffects(): void {
    effect(() => {
      // if (this.screenSizeChanges$() > 1) {
        this.#initURLs();
      // }
    });
  }

  #initializeRequestedFormats(): void {
    this.requestedFormats = Array.from(new Set(Object.values(this.imageReference().formatMap)));
  }

  #initializeAltText(): void {
    this.altText$ = this.stateService.getImageAltText(this.elemId());
  }

  #initURLs(): void {
    const refSystem = this.imageReference().coordinatesSystem;
    const formats = this.imageReference().formatMap;
    const shopID = this.shopID();
    const elemId = this.elemId();

    if (!refSystem || !formats || !shopID || !elemId) return;

    const ref = this.screenSize$()[refSystem] as keyof FormatMapInput;
    const format = formats[ref] ? formats[ref] : ImageFormat.SixteenToNine;
    const urls = this.stateService.getDownloadURLs(elemId, format, FileType.WEBP);
    this.imageSRC$.set(urls);
  }


  // ### Overlay methods for dialog portal ###
  openPortal() {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      backdropClass: 'cdk-overlay-dark-backdrop', // Add a slightly darker backdrop
    });

    const portal = new ComponentPortal(ImageCropComponent);
    const componentRef = this.overlayRef.attach(portal);

    componentRef.instance.elemId = this.elemId();
    componentRef.instance.requestedFormats = this.requestedFormats;
    componentRef.instance.fileTypes = this.fileTypes();
    componentRef.instance.maxWidth = this.maxWidth();
    componentRef.instance.maxHeight = this.maxHeight();
    // componentRef.instance.originalImage = this.originalImage;


    this.overlaySubscription.add(
      componentRef.instance.imageSaved.subscribe(() => {
        this.reloadImage();
      })
    );

    this.overlaySubscription.add(
      componentRef.instance.closePortal.subscribe(() => {
        this.closeOverlay();
      })
    );

    this.overlaySubscription.add(
      this.overlayRef.backdropClick().subscribe(() => {
        this.closeOverlay();
      })
    );
  }

  closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  public reloadImage(): void {
    this.reloadingImage = true;

    // Create a unique query parameter to force reload
    const currentUrls = this.imageSRC$();
    const newUrls = Object.keys(currentUrls).reduce((acc, key) => {
      acc[key] = `${currentUrls[key]}?timestamp=${new Date().getTime()}`;
      return acc;
    }, {} as Record<string, string>);

    this.imageSRC$.set(newUrls);
  }

  onImageLoad(): void {
    this.reloadingImage = false;
  }

  onImageError(): void {
    this.imageSRC$.set({ 'type': "png", 'url': '/assets/placeholder-image.png' });
    this.reloadingImage = false;
  }
}
